import { useMemo } from 'react';
import track from 'react-tracking';
import { useSelector } from 'react-redux';
import If from '@/components/If';
import GlobalStyle from '@/styled/styled.default';
import { Sentry } from '@/components/Sentry';
import loadable from '@loadable/component';
import { useStatuspage } from '@/hooks/useStatuspage';
import { useDatadogRum } from '@/hooks/useDatadogRum';
import { useGTM } from '@/hooks/useGTM';

import { useSetupRewardful } from '@/features/rewardful';
import { getCurrentRoute } from '@/utilities/sentry/utils';

const ContentContainer = loadable(
  () => import('@/containers/ContentContainer')
);
const NotFound = loadable(() => import('@/pages/NotFound'));
const Oops = loadable(() => import('@/components/Oops'));
const Timeout = loadable(() => import('@/components/Timeout'));
const Maintenance = loadable(() => import('@/pages/Maintenance'));

export interface Props {
  pageName: string;
}

const SetupScripts = () => {
  useDatadogRum(
    getCurrentRoute(),
    '@sws/web',
    '8a5a2c05-e709-4595-a937-018ad6ec962f'
  );
  useStatuspage();
  useSetupRewardful();
  useGTM();
  return null;
};

const SimplyWallSt = (props: Props) => {
  const MemoGlobalStyle = useMemo(() => <GlobalStyle />, [props.pageName]);
  const pageStatus = useSelector((state: any) => state.page.pageStatus);

  return (
    <>
      {MemoGlobalStyle}
      <Sentry>
        <If
          conditions={{
            notFound: pageStatus === 404,
            timedOut: pageStatus === 408,
            success: pageStatus >= 200 && pageStatus < 300,
            serverError:
              pageStatus >= 400 &&
              pageStatus < 600 &&
              pageStatus !== 503 &&
              pageStatus !== 404 &&
              pageStatus !== 408, // generic error
            maintenance: pageStatus === 503,
          }}
        >
          {{
            notFound: () => <NotFound />,
            timedOut: () => <Timeout />,
            success: () => <ContentContainer />,
            serverError: () => <Oops />,
            maintenance: () => <Maintenance />,
          }}
        </If>
        <SetupScripts />
      </Sentry>
    </>
  );
};

export default track(
  ({ pageName }) => ({
    page: pageName,
  }),
  { dispatchOnMount: true }
)(SimplyWallSt) as typeof SimplyWallSt;
